// Submit / order modal for the Designer, extracted verbatim from Designer.tsx.
// Two-step flow over the shared `submit` state machine (owned by the page via
// useDesignerSubmit): (1) confirm the per-location proofs, then (2) capture
// shipping + place the order. Presentational — proofs, shipping state and
// placeOrder all forward via props. Renders nothing while submit.open is false.
import type { Dispatch, SetStateAction } from 'react'
import type { CatProduct, CatColour } from '../../lib/catalog-types'
import type { GarmentTotals } from './helpers'
import type { SubmitState, ShippingForm } from './useDesignerSubmit'

export interface SubmitOrderModalProps {
  submit: SubmitState
  setSubmit: Dispatch<SetStateAction<SubmitState>>
  shipping: ShippingForm
  setShipping: Dispatch<SetStateAction<ShippingForm>>
  placeOrder: () => void
  product: CatProduct
  colour: CatColour | undefined
  totals: GarmentTotals
  hasUser: boolean
}

export default function SubmitOrderModal({
  submit, setSubmit, shipping, setShipping, placeOrder, product, colour, totals, hasUser,
}: SubmitOrderModalProps) {
  if (!submit.open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full overflow-hidden max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-border flex items-center justify-between">
          <div>
            <h3 className="text-ink font-heading uppercase font-bold text-lg">{submit.confirmed ? 'Place your order' : 'Confirm your design'}</h3>
            <p className="text-xs text-ink-3">{submit.confirmed
              ? "Review the shipping details, then we'll receive it into production."
              : 'Each printed side is shown below. Confirm everything looks correct before we send it to production.'}</p>
          </div>
          <button onClick={() => setSubmit({ open: false, confirmed: false, busy: false })} className="text-ink-3 hover:text-ink text-2xl leading-none">×</button>
        </div>

        {/* Confirmation step — shown until the customer clicks "Looks right" */}
        {!submit.confirmed && (
          <div className="p-6 space-y-4">
            {submit.proofs && submit.proofs.length > 0 ? (
              <>
                <div className={'grid gap-4 ' + (submit.proofs.length === 1 ? '' : 'sm:grid-cols-2')}>
                  {submit.proofs.map((p) => (
                    <div key={p.locationId} className="border border-border rounded overflow-hidden bg-surface">
                      <div className="px-3 py-2 bg-white border-b border-border flex items-center justify-between">
                        <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3">Print location</div>
                        <div className="text-sm font-semibold text-ink">{p.locationName}</div>
                      </div>
                      <img src={p.dataUrl} alt={`Proof - ${p.locationName}`} className="w-full block" />
                    </div>
                  ))}
                </div>
                <p className="text-xs text-ink-3 text-center">
                  {submit.proofs.length} print location{submit.proofs.length === 1 ? '' : 's'} ready · garment {product.name} · colour {colour?.name}
                </p>
              </>
            ) : (
              <p className="text-sm text-ink-3 text-center py-10">Generating preview…</p>
            )}
            <div className="flex justify-between items-center gap-3 flex-wrap pt-2 border-t border-border">
              <button onClick={() => setSubmit({ open: false, confirmed: false, busy: false })}
                className="btn-outline text-xs">
                Go back to edit
              </button>
              <button onClick={() => setSubmit((s) => ({ ...s, confirmed: true }))}
                disabled={!submit.proofs || submit.proofs.length === 0}
                className="btn-gold text-xs disabled:opacity-50 disabled:cursor-not-allowed">
                Looks right - continue to shipping
              </button>
            </div>
          </div>
        )}

        {/* Order step — only after confirmation */}
        {submit.confirmed && (
        <div className="p-6 grid md:grid-cols-2 gap-6">
          <div>
            <div className="border border-border rounded overflow-hidden bg-surface">
              {submit.proof && <img src={submit.proof} alt="Proof" className="w-full block" />}
            </div>
            {submit.proofs && submit.proofs.length > 1 && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {submit.proofs.map((p) => (
                  <div key={p.locationId} className="border border-border rounded p-1 bg-white">
                    <img src={p.dataUrl} alt={p.locationName} className="w-16 h-16 object-contain block" />
                    <div className="text-[10px] uppercase tracking-wider text-ink-3 text-center mt-1">{p.locationName}</div>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-ink-3">Garment</span><span className="text-ink font-semibold">{product.name}</span></div>
              <div className="flex justify-between"><span className="text-ink-3">Colour</span><span className="text-ink font-semibold">{colour?.name}</span></div>
              <div className="flex justify-between"><span className="text-ink-3">Total</span><span className="text-ink font-bold">${totals.total.toFixed(2)}</span></div>
            </div>
          </div>
          <div className="space-y-3 text-sm">
            <div className="eyebrow">Shipping</div>
            <input className="input" placeholder="Full name" value={shipping.fullName} onChange={(e) => setShipping({ ...shipping, fullName: e.target.value })} />
            <input className="input" placeholder="Email" type="email" value={shipping.email} onChange={(e) => setShipping({ ...shipping, email: e.target.value })} />
            <input className="input" placeholder="Phone (optional)" value={shipping.phone} onChange={(e) => setShipping({ ...shipping, phone: e.target.value })} />
            <input className="input" placeholder="Street address" value={shipping.line1} onChange={(e) => setShipping({ ...shipping, line1: e.target.value })} />
            <input className="input" placeholder="Apartment, suite, etc. (optional)" value={shipping.line2} onChange={(e) => setShipping({ ...shipping, line2: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <input className="input" placeholder="Suburb" value={shipping.city} onChange={(e) => setShipping({ ...shipping, city: e.target.value })} />
              <input className="input" placeholder="State" value={shipping.region} onChange={(e) => setShipping({ ...shipping, region: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input className="input" placeholder="Postcode" value={shipping.postcode} onChange={(e) => setShipping({ ...shipping, postcode: e.target.value })} />
              <input className="input" placeholder="Country" value={shipping.country} onChange={(e) => setShipping({ ...shipping, country: e.target.value.toUpperCase().slice(0, 2) })} />
            </div>
            <button onClick={placeOrder} disabled={submit.busy} className="btn-gold w-full justify-center mt-2">
              {submit.busy ? 'Submitting…' : 'Place order'}
            </button>
            <p className="text-[11px] text-ink-3 leading-relaxed">Payment integration is coming — for now the order is placed as <strong>pending payment</strong> and EVRST Studio will follow up with an invoice. {!hasUser && <>Sign in next time to track orders in one place.</>}</p>
          </div>
        </div>
        )}
      </div>
    </div>
  )
}
